import { useStudio } from "./StudioContext";
import { Panel, SectionLabel, Badge } from "./StudioShared";
import { Activity, Database, Mail, Server, ShieldCheck, GitBranch, Clock, Tag } from "lucide-react";

const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

const toneFor = (kind, value) => {
  if (kind === "platform") return value === "ok" ? "green" : value === "degraded" ? "amber" : "red";
  if (kind === "db") return value === "connected" ? "green" : "red";
  if (kind === "email") return value === "enabled" ? "green" : "amber";
  if (kind === "health") return value === "healthy" ? "green" : value === "missing_api_key" ? "slate" : "amber";
  return "slate";
};

const StatusRow = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid={testid}>
    <div className="flex items-center gap-2.5 text-slate-400 text-sm">
      <Icon size={15} className="text-gold shrink-0" /> {label}
    </div>
    <div className="text-right">{children}</div>
  </div>
);

export default function SystemStatus() {
  const { sysHealth, providers } = useStudio();
  const checks = sysHealth?.checks || {};
  const ai = checks.ai_providers || {};
  const providerList = providers?.providers || [];
  const failoverMode = providers?.failover_status || "—";
  const lastCheck = providerList.find((p) => p.last_health_check)?.last_health_check || sysHealth?.time;

  const emailLabel = checks.email === "enabled" ? "Enabled"
    : checks.email === "disabled_or_pending_key" ? "Pending Key" : "Unknown";

  return (
    <Panel className="p-6 mb-6" data-testid="system-status-card">
      <div className="flex items-center gap-2 mb-5">
        <Activity size={16} className="text-gold" />
        <h2 className="font-heading text-lg text-white">System Status</h2>
        <span className="text-xs text-slate-500 ml-auto">operational visibility · admin only</span>
      </div>

      <div className="grid md:grid-cols-2 gap-x-10">
        <div>
          <StatusRow icon={Activity} label="Platform Status" testid="status-platform">
            {sysHealth ? (
              <Badge tone={toneFor("platform", sysHealth.status)} testid="badge-platform">{titleCase(sysHealth.status)}</Badge>
            ) : <span className="text-slate-500 text-sm">—</span>}
          </StatusRow>
          <StatusRow icon={Database} label="Database" testid="status-database">
            <Badge tone={toneFor("db", checks.database)} testid="badge-database">{titleCase(checks.database) || "—"}</Badge>
          </StatusRow>
          <StatusRow icon={Mail} label="Email Service" testid="status-email">
            <Badge tone={toneFor("email", checks.email)} testid="badge-email">{emailLabel}</Badge>
          </StatusRow>
          <StatusRow icon={GitBranch} label="Failover Mode" testid="status-failover">
            <span className="text-slate-300 text-sm capitalize">{failoverMode.replace(/_/g, " ")}</span>
          </StatusRow>
        </div>

        <div>
          <StatusRow icon={Server} label="Active AI Providers" testid="status-active-providers">
            <span className="font-mono text-sm text-white" data-testid="active-provider-count">
              {ai.count != null ? `${ai.count}` : "—"}
              {ai.available?.length ? <span className="text-slate-500"> · {ai.available.map(titleCase).join(", ")}</span> : null}
            </span>
          </StatusRow>
          <StatusRow icon={Tag} label="Version" testid="status-version">
            <span className="font-mono text-sm text-slate-300" data-testid="app-version">{sysHealth?.version || "—"}</span>
          </StatusRow>
          <StatusRow icon={Clock} label="Last Health Check" testid="status-last-check">
            <span className="text-sm text-slate-300" data-testid="last-health-check">
              {lastCheck ? new Date(lastCheck).toLocaleString() : "—"}
            </span>
          </StatusRow>
        </div>
      </div>

      {/* Per-provider health */}
      <div className="mt-5 pt-4 border-t border-white/5">
        <SectionLabel className="mb-3 flex items-center gap-2"><ShieldCheck size={13} className="text-gold" /> Provider Health</SectionLabel>
        <div className="flex flex-wrap gap-2" data-testid="provider-health-list">
          {providerList.length ? providerList.map((p) => (
            <Badge key={p.name} tone={toneFor("health", p.health)} testid={`provider-health-${p.name}`}>
              {titleCase(p.name)} · {(p.health || "unknown").replace(/_/g, " ")}
            </Badge>
          )) : <span className="text-slate-500 text-sm">No provider data.</span>}
        </div>
      </div>
    </Panel>
  );
}
